import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Bug, Lightbulb, RefreshCw, Clock, CheckCircle, XCircle, PlayCircle, Hourglass, Download, ZoomIn, X } from 'lucide-react';
import api from '../services/api';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
const fullUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  if (url.startsWith('http')) return url;
  return `${API_BASE}${url}`;
};

const downloadImage = async (url, filename) => {
  try {
    const res = await fetch(fullUrl(url));
    const blob = await res.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = filename || 'screenshot.jpg';
    a.click();
    URL.revokeObjectURL(a.href);
  } catch {
    window.open(fullUrl(url), '_blank');
  }
};

const STATUS_CONFIG = {
  pending:     { label: 'Under Review', color: 'bg-gray-100 text-gray-600',    Icon: Hourglass },
  approved:    { label: 'Approved',     color: 'bg-blue-100 text-blue-700',    Icon: CheckCircle },
  in_progress: { label: 'In Progress',  color: 'bg-amber-100 text-amber-700',  Icon: PlayCircle },
  done:        { label: 'Resolved',     color: 'bg-green-100 text-green-700',  Icon: CheckCircle },
  rejected:    { label: 'Rejected',     color: 'bg-red-100 text-red-700',      Icon: XCircle },
};

const TYPE_CONFIG = {
  bug:     { label: 'Bug', color: 'bg-red-50 text-red-600 border-red-200',     Icon: Bug },
  feature: { label: 'Feature', color: 'bg-purple-50 text-purple-600 border-purple-200', Icon: Lightbulb },
};

const formatDate = (iso) => {
  if (!iso) return '';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
    timeZone: 'Asia/Kolkata',
  });
};

export default function MyFeedbackStatus() {
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const res = await api.get('/feedback/my-tickets');
      setTickets(res.data || []);
    } catch {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    // Mark feedback as read when user opens My Reports
    api.post('/feedback/mark-read').catch(() => {});
  }, []);

  return (
    <>
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-[#075E54] text-white px-4 py-3 flex items-center gap-3 sticky top-0 z-10">
        <button onClick={() => navigate(-1)} className="p-1 rounded hover:bg-emerald-700 transition-colors" data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="font-bold text-base">My Reports</h1>
          <p className="text-xs text-emerald-200">{tickets.length} submission{tickets.length !== 1 ? 's' : ''}</p>
        </div>
        <button onClick={fetchTickets} className="p-1.5 rounded hover:bg-emerald-700 transition-colors" data-testid="refresh-btn">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      <div className="flex-1 p-4 max-w-xl mx-auto w-full">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <RefreshCw className="w-6 h-6 text-emerald-600 animate-spin" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <Bug className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm font-medium">No submissions yet</p>
            <p className="text-xs mt-1">Use the <span className="text-emerald-600 font-semibold">+ Report</span> button to report a bug or suggest a feature.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {tickets.map((t) => {
              const status = STATUS_CONFIG[t.status] || STATUS_CONFIG.pending;
              const type = TYPE_CONFIG[t.type] || TYPE_CONFIG.bug;
              const TypeIcon = type.Icon;
              const StatusIcon = status.Icon;
              return (
                <div key={t.ticket_id} className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid={`ticket-${t.ticket_id}`}>
                  {/* Top bar */}
                  <div className="px-4 pt-3 pb-2 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${type.color}`}>
                        <TypeIcon className="w-3 h-3" />
                        {type.label}
                      </span>
                      <span className="text-[11px] text-gray-400 font-mono">{t.ticket_id}</span>
                    </div>
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${status.color}`}>
                      <StatusIcon className="w-3 h-3" />
                      {status.label}
                    </span>
                  </div>

                  {/* Description */}
                  <div className="px-4 pb-3">
                    <p className="text-sm text-gray-800 leading-snug">{t.description}</p>
                    <p className="text-[11px] text-gray-400 mt-1.5 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      Submitted {formatDate(t.created_at)}
                    </p>
                  </div>

                  {/* Screenshots */}
                  {t.screenshot_urls?.length > 0 && (
                    <div className="px-4 pb-3 space-y-2">
                      {t.screenshot_urls.map((url, i) => (
                        <div key={i} className="relative rounded-xl overflow-hidden border border-gray-200 bg-gray-50">
                          <img
                            src={fullUrl(url)}
                            alt={`Screenshot ${i + 1}`}
                            className="w-full object-contain max-h-48 cursor-zoom-in"
                            onClick={() => setLightboxUrl(fullUrl(url))}
                          />
                          <div className="absolute top-2 right-2 flex gap-1.5">
                            <button
                              onClick={() => setLightboxUrl(fullUrl(url))}
                              className="bg-black/50 hover:bg-black/70 text-white rounded-lg p-1.5 transition-colors"
                              title="View full size"
                            >
                              <ZoomIn className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => downloadImage(url, `${t.ticket_id}-screenshot-${i + 1}.jpg`)}
                              className="bg-black/50 hover:bg-black/70 text-white rounded-lg p-1.5 transition-colors"
                              title="Save photo"
                            >
                              <Download className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Admin note */}
                  {t.admin_note && (
                    <div className="mx-4 mb-3 p-2.5 bg-blue-50 border border-blue-100 rounded-lg">
                      <p className="text-[11px] font-semibold text-blue-600 mb-0.5">Note from Admin</p>
                      <p className="text-xs text-blue-800">{t.admin_note}</p>
                    </div>
                  )}

                  {/* Resolution */}
                  {t.resolution_summary && (
                    <div className="mx-4 mb-3 p-2.5 bg-green-50 border border-green-100 rounded-lg">
                      <p className="text-[11px] font-semibold text-green-600 mb-0.5">Resolution</p>
                      <p className="text-xs text-green-800">{t.resolution_summary}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>

    {/* Lightbox */}
    {lightboxUrl && (
      <div
        className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
        onClick={() => setLightboxUrl(null)}
      >
        <div className="relative max-w-full max-h-full" onClick={e => e.stopPropagation()}>
          <img src={lightboxUrl} alt="Full size screenshot" className="max-w-[90vw] max-h-[85vh] object-contain rounded-xl" />
          <div className="absolute top-2 right-2 flex gap-2">
            <button
              onClick={() => downloadImage(lightboxUrl, 'screenshot.jpg')}
              className="bg-white/20 hover:bg-white/40 text-white rounded-xl px-3 py-2 flex items-center gap-1.5 text-sm font-medium transition-colors backdrop-blur-sm"
            >
              <Download className="w-4 h-4" /> Save Photo
            </button>
            <button
              onClick={() => setLightboxUrl(null)}
              className="bg-white/20 hover:bg-white/40 text-white rounded-xl p-2 transition-colors backdrop-blur-sm"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}
